import type { HTMLAttributes, ReactNode } from 'react';
import { cx } from '@/lib/cx';
import s from './Card.module.css';

export interface CardProps extends Omit<HTMLAttributes<HTMLElement>, 'title'> {
  eyebrow?: ReactNode;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  footer?: ReactNode;
  /** Featured variant: a 3px left rule (blue; amber for warnings). */
  accent?: 'primary' | 'warning' | 'danger';
  padding?: 'none' | 'sm' | 'md';
  as?: 'section' | 'article' | 'div';
}

/** Surface card: 1px border, 6px radius. Borders do the work; no heavy shadows. */
export function Card({ eyebrow, title, description, actions, footer, accent, padding = 'md', as: Tag = 'section', className, children, ...rest }: CardProps) {
  const hasHeader = eyebrow || title || actions || description;
  return (
    <Tag className={cx(s.card, accent && s[`accent-${accent}`], className)} {...rest}>
      {hasHeader && (
        <header className={s.header}>
          <div className={s.heading}>
            {eyebrow && <div className={s.eyebrow}>{eyebrow}</div>}
            {title && <h2 className={s.title}>{title}</h2>}
            {description && <p className={s.description}>{description}</p>}
          </div>
          {actions && <div className={s.actions}>{actions}</div>}
        </header>
      )}
      {children != null && <div className={cx(s.body, s[`pad-${padding}`], !hasHeader && s.bodyOnly)}>{children}</div>}
      {footer && <footer className={s.footer}>{footer}</footer>}
    </Tag>
  );
}
